import { useCallback, useEffect, useRef, useState } from 'react'
import {
  appConfigResponseSchema,
  manufacturingOperatorActionSchema,
  manufacturingFactoryActionSchema,
  manufacturingOperatorStateSchema,
  manufacturingFactoryStateSchema,
  type ManufacturingOperatorState,
  type ManufacturingFactoryState,
} from '@repo/shared'
import { request, json } from '../api'
import { env } from '../env'
import { useGetToken, useAdminSessionKey, type GetToken } from './auth'

export function useManufacturingFeature() {
  const [enabled, setEnabled] = useState<boolean | null>(null)
  useEffect(() => {
    let alive = true
    void fetch(`${env.API_URL}/config`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((raw) => {
        const c = appConfigResponseSchema.parse(raw)
        if (alive)
          setEnabled(c.features?.manufacturing === true && c.settings.manufacturing.enabled)
      })
      .catch(() => {
        if (alive) setEnabled(false)
      })
    return () => {
      alive = false
    }
  }, [])
  return enabled
}

export function useManufacturingState<T extends 'operator' | 'factory'>(role: T) {
  const getToken = useGetToken(),
    session = useAdminSessionKey()
  const [state, setState] = useState<ManufacturingOperatorState | ManufacturingFactoryState | null>(
    null,
  )
  const [error, setError] = useState(false)
  const [mutatingScope, setMutatingScope] = useState<string | null>(null)
  const [mutationErrorScope, setMutationErrorScope] = useState<string | null>(null)
  const mutationScope = useRef<string | null>(null)
  const scope = `${session}:${role}`,
    currentScope = useRef(scope),
    sequence = useRef(0)
  currentScope.current = scope
  const [savedScope, setSavedScope] = useState(scope)
  const path = role === 'operator' ? '/admin/manufacturing-sync' : '/manufacturing-sync/factory'
  const readState = useCallback(async () => {
    const requestSequence = ++sequence.current
    try {
      const raw = await request<unknown>(getToken, path)
      const data =
        role === 'operator'
          ? manufacturingOperatorStateSchema.parse(raw)
          : manufacturingFactoryStateSchema.parse(raw)
      if (currentScope.current === scope && sequence.current === requestSequence) {
        setState(data)
        setSavedScope(scope)
        setError(false)
      }
    } catch (e) {
      if (currentScope.current === scope && sequence.current === requestSequence) {
        setState(null)
        setSavedScope(scope)
        setError(true)
      }
      throw e
    }
  }, [getToken, path, role, scope])
  const reload = useCallback(async () => {
    if (mutationScope.current === scope) return
    await readState()
  }, [readState, scope])
  const mutate = useCallback(
    async <Result>(action: () => Promise<Result>) => {
      if (mutationScope.current === scope)
        throw new Error('Manufacturing action is already pending')
      mutationScope.current = scope
      sequence.current++
      setMutatingScope(scope)
      setMutationErrorScope(null)
      try {
        const result = await action()
        // Keep every form locked until the accepted job appears in the server state.
        if (currentScope.current === scope) await readState()
        return result
      } catch (e) {
        if (currentScope.current === scope) setMutationErrorScope(scope)
        throw e
      } finally {
        if (mutationScope.current === scope) mutationScope.current = null
        if (currentScope.current === scope) setMutatingScope(null)
      }
    },
    [readState, scope],
  )
  useEffect(() => {
    let alive = true,
      loading = false
    setState(null)
    setError(false)
    const load = async () => {
      if (loading) return
      loading = true
      try {
        if (alive) await reload()
      } catch {
        // reload clears stale private data and exposes the error to the caller.
      } finally {
        loading = false
      }
    }
    void load()
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void load()
    }, 5000)
    return () => {
      alive = false
      sequence.current++
      clearInterval(timer)
    }
  }, [reload, session])
  return {
    state: (savedScope === scope ? state : null) as
      (T extends 'operator' ? ManufacturingOperatorState : ManufacturingFactoryState) | null,
    error: savedScope === scope && error,
    reload,
    mutate,
    mutating: mutatingScope === scope,
    mutationError: mutationErrorScope === scope,
  }
}

export async function manufacturingAction(
  getToken: GetToken,
  role: 'operator' | 'factory',
  body: Record<string, unknown>,
) {
  const key = `manufacturing-action:${role}`,
    fingerprint = JSON.stringify(body)
  let operationId: string = crypto.randomUUID()
  try {
    const prior = JSON.parse(sessionStorage.getItem(key) ?? 'null') as {
      fingerprint: string
      id: string
    } | null
    if (prior?.fingerprint === fingerprint) operationId = prior.id
    sessionStorage.setItem(key, JSON.stringify({ fingerprint, id: operationId }))
  } catch {
    /* Retry still works while storage is unavailable. */
  }
  const schema =
    role === 'operator' ? manufacturingOperatorActionSchema : manufacturingFactoryActionSchema
  const input = schema.parse({ ...body, operation_id: operationId })
  const result = await request<{ job: { id: string; status: string } }>(
    getToken,
    role === 'operator' ? '/admin/manufacturing-sync/actions' : '/manufacturing-sync/factory',
    json('POST', input),
  )
  try {
    const saved = JSON.parse(sessionStorage.getItem(key) ?? 'null') as { id: string } | null
    if (saved?.id === operationId) sessionStorage.removeItem(key)
  } catch {
    // A lost response retains the ID; an accepted operation may be submitted anew later.
  }
  return result
}
